import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';
import Card from 'react-bootstrap/Card';
import { Link, useLocation } from 'react-router';


import ResourceTooltip from '@src/components/common/Display/ResourceTooltip';
import StatusLabels from '@src/components/common/Display/titles/StatusLabels';
import { getMonthDayYearWithNoLeadingZeros } from '@src/services';

import './TileContainerBasic.scss';

const TileContainerBasic = ({
    categories,
    children,
    description,
    ends_at,
    expired_at,
    id,
    resource_type,
    image,
    platforms,
    products,
    published_at,
    readTime,
    relatedArticles,
    slug,
    starts_at,
    states,
    status,
    title,
    urlPrefix,
    columns = 4,
}) => {
    const { pathname } = useLocation();

    const renderPlatformIndicator = () => {
        if (!categories && !platforms) {
            return null;
        }

        if (platforms && platforms.length > 0) {
            return (
                <div className="platforms-block">
                    {
                        platforms.map((platform) => {
                            return (
                                <div key={platform.id} className="platform-indicator">
                                    <span className={`platform-box-${platform.slug === 'agent360' ? 'blue' : 'red'}`} />
                                    <span className="platform-text">{platform.name}</span>
                                </div>
                            );
                        })
                    }
                </div>
            );
        } else if (categories && categories.length > 0) {
            return (
                <div className="platforms-block">
                    {
                        categories.map((category) => {
                            return (
                                <div key={category.id} className="platform-indicator">
                                    <span className={`platform-box-${category.slug === 'agent360' ? 'blue' : 'red'}`} />
                                    <span className="platform-text">{category.name}</span>
                                </div>
                            );
                        })
                    }
                </div>
            );
        }

        return null;
    };

    const articleLink = () => {
        let prefix;

        if (urlPrefix) {
            prefix = urlPrefix.startsWith('/') ? urlPrefix : `/${urlPrefix}`;
        } else {
            prefix = pathname === '/' ? '' : pathname;
        }

        prefix = prefix.charAt(prefix.length - 1) === '/' ? prefix.slice(0, -1) : prefix;

        return `${prefix}/${slug}`;
    };

    const linkText = () => {

        if (readTime) {
            return `${readTime} Minute Read`;
        }

        // if (hasVideo) {
        //     return 'Watch video';
        // }

        return 'Read More';
    };

    const readMoreLink = () => {
        const articleLinkUrl = articleLink();
        const cardTitle = linkText() === 'Read More' ? `Read more about ${title}` : title;

        return (
            <Link
                to={articleLinkUrl}
                className="btn btn-outline-primary stretched-link"
                title={cardTitle}
                aria-label={cardTitle}
            >
                { linkText() }
            </Link>
        );
    };

    const renderTooltip = () => {

        return (
            <ResourceTooltip
                placement="left"
                publishedAt={publishedAt}
                products={products}
                states={states}
                resourceType={resource_type}
                resourceId={id}
            />
        );
    };

    const endsAt = ends_at;
    const expiredAt = expired_at;
    const publishedAt = published_at;
    const startsAt = starts_at;
    const isWebinar = (urlPrefix && urlPrefix.includes('webinars')) || resource_type === 'Webinar';

    let divClassNames = classNames({
        'col-sm-12 col-md-6 col-lg-3 mb-3 mb-md-4 mb-lg-4': relatedArticles,
        'col-sm-12 col-md-4 col-lg-3 mb-3 mb-md-4 mb-lg-4': !relatedArticles,
    });

    if (columns === 3) {
        divClassNames = classNames({
            'col-sm-12 col-md-6 col-lg-4 mb-3 mb-md-4 mb-lg-4': true,
        });
    }

    const cardClassNames = classNames({
        'card-tile-container-basic': true,
        // 'has-video': hasVideo,
    });

    return (
        <div id={`tile-container-basic-${id}`} className={divClassNames}>
            <Card className={cardClassNames}>
                <Card.Body key={`card-body-${id}`}>
                    <Card.Title
                        key={`card-img-${id}`}
                        id={`card-img-${id}`}
                        as="div"
                    />
                    <h5
                        key={`card-body-title-${id}`}
                        id={`card-body-title-${id}`}
                        className="title text-start"
                    >
                        <StatusLabels
                            key={`card-body-status-label-${id}`}
                            expiredAt={expiredAt}
                            status={status}
                            publishedAt={publishedAt}
                        />
                        { title }
                    </h5>
                    {
                        (() => {
                            let childrenCount;
                            if (children) {
                                childrenCount = React.Children.count(children);
                                return React.Children.map(children, (child, i) => {
                                    if (childrenCount === 1) {
                                        return null;
                                    }

                                    if (childrenCount === 2 && i === 0) {
                                        return child;
                                    }
                                });
                            }

                            return (
                                <>
                                    {
                                        description
                                            && <p>{description}</p>
                                    }
                                    {
                                        ((startsAt && endsAt) || isWebinar) && (
                                            <div className="start-end">
                                                <span>
                                                    {
                                                        (startsAt && endsAt)
                                                            ? `${getMonthDayYearWithNoLeadingZeros(startsAt)} - ${getMonthDayYearWithNoLeadingZeros(endsAt)}`
                                                            : 'Dates and times will vary'
                                                    }
                                                </span>
                                            </div>
                                        )
                                    }
                                </>
                            );
                        })()
                    }
                </Card.Body>
                <Card.Footer className="border-0 pb-4">
                    {renderPlatformIndicator()}
                    {
                        (() => {
                            let childrenCount;
                            if (children) {
                                childrenCount = React.Children.count(children);
                                return React.Children.map(children, (child, i) => {
                                    // Ignore the first child
                                    if (childrenCount === 1) {
                                        return child;
                                    }

                                    if (childrenCount === 2 && i === 1) {
                                        return child;
                                    }
                                });
                            }

                            return readMoreLink();
                        })()
                    }
                    {renderTooltip()}
                </Card.Footer>
            </Card>
        </div>
    );
};

TileContainerBasic.propTypes = {
    categories: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        slug: PropTypes.string.isRequired,
        taxonomy_id: PropTypes.number,
    })),
    children: PropTypes.node,
    description: PropTypes.string,
    ends_at: PropTypes.string,
    expired_at: PropTypes.string,
    id: PropTypes.number,
    resource_type: PropTypes.string,
    image: PropTypes.shape({
        alt: PropTypes.string.isRequired,
        source: PropTypes.string.isRequired,
        versions: PropTypes.shape({
            w400: PropTypes.string,
            w700: PropTypes.string,
            w1200: PropTypes.string,
        }),
    }),
    platforms: PropTypes.arrayOf(PropTypes.object),
    products: PropTypes.arrayOf(PropTypes.object),
    published_at: PropTypes.string,
    readTime: PropTypes.number,
    relatedArticles: PropTypes.bool,
    slug: PropTypes.string.isRequired,
    starts_at: PropTypes.string,
    states: PropTypes.arrayOf(PropTypes.exact({
        id: PropTypes.number.isRequired,
        slug: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        abbreviation: PropTypes.string.isRequired,
    })).isRequired,
    status: PropTypes.string.isRequired,
    title: PropTypes.node.isRequired,
    urlPrefix: PropTypes.string,
    columns: PropTypes.number,
};

export default TileContainerBasic;
